import { csvAmount, type CsvColumn } from '@/utils/csv';
import type { ContractProgress, ContractSubscriptionInfo } from '../hooks/useContractsPayments';

/**
 * Formatage de la fiche détail d'un contrat, partagé par la fiche
 * (ContractDetailModal) et l'export de l'onglet Contrats : le CSV reprend
 * exactement les valeurs affichées.
 */

export const euroEur = (n?: number | null) =>
  n == null
    ? '—'
    : `${n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;

export const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

export const fmtUnix = (u?: number | null) => (u ? new Date(u * 1000).toLocaleDateString('fr-FR') : '—');

export const CONTRACT_STATUS: Record<number, string> = {
  1: 'Actif',
  2: 'Expiré',
  3: 'Annulé',
  4: 'Suspendu',
};

export const SUB_STATUS: Record<string, string> = {
  active: 'Active',
  past_due: 'Impayé',
  canceled: 'Annulée',
  unpaid: 'Impayée',
  trialing: 'Essai',
  incomplete: 'Incomplète',
  incomplete_expired: 'Expirée',
  paused: 'En pause',
};

export function paymentMode(id?: string | null): string {
  if (!id) return 'Gratuit / comptant agence';
  if (id.startsWith('sub_') || id.startsWith('seti_')) return 'Mensuel';
  if (id.startsWith('pi_')) return 'Comptant en ligne';
  return 'Autre';
}

/** Fiche d'un contrat : `GET /pro-v2/contracts/:id/payments` sans `payments`. */
export interface ContractSummaryData {
  contract: any;
  subscription: ContractSubscriptionInfo | null;
  progress: ContractProgress | null;
}

export const beneficiaryName = (beneficiary?: { firstName?: string; lastName?: string } | null) =>
  beneficiary ? `${beneficiary.firstName ?? ''} ${beneficiary.lastName ?? ''}`.trim() || '—' : '—';

export const guarantorAddress = (
  guarantor?: { address?: string; cp?: string; city?: string } | null,
) =>
  [guarantor?.address, `${guarantor?.cp ?? ''} ${guarantor?.city ?? ''}`.trim()]
    .filter(Boolean)
    .join(', ') || '—';

export const subscriptionAmount = (contract?: {
  monthlyPriceClient?: number | null;
  sellingPrice?: number | null;
}) =>
  contract?.monthlyPriceClient != null
    ? `${euroEur(contract.monthlyPriceClient)} /mois`
    : euroEur(contract?.sellingPrice);

export const durationLabel = (duration?: number | null) => (duration ? `${duration} mois` : '—');

export const contractStatusLabel = (status?: number | null) =>
  status == null ? '—' : CONTRACT_STATUS[status] ?? String(status);

export const subscriptionStatusLabel = (subscription?: ContractSubscriptionInfo | null) =>
  subscription ? SUB_STATUS[subscription.status] ?? subscription.status : '—';

export const paidInstallments = (progress?: ContractProgress | null) =>
  progress ? `${progress.paidCount} / ${progress.totalCount}` : '—';

/** Valeur de cellule : un tiret d'absence à l'écran devient une cellule vide. */
const cell = (value: string | null | undefined) => (value == null || value === '—' ? '' : value);

/** Export de l'onglet Contrats : la fiche de chaque contrat, sans sa liste de paiements. */
export const CONTRACT_SUMMARY_CSV_COLUMNS: CsvColumn<ContractSummaryData>[] = [
  { header: 'N° contrat', value: (d) => d.contract.label },
  // Bénéficiaire
  { header: 'Bénéficiaire', value: (d) => cell(beneficiaryName(d.contract.beneficiary)) },
  { header: 'Bénéficiaire e-mail', value: (d) => d.contract.beneficiary?.email },
  { header: 'Bénéficiaire téléphone', value: (d) => d.contract.beneficiary?.phoneNumber },
  // Garant
  { header: 'Garant', value: (d) => d.contract.society?.name },
  { header: 'Garant e-mail', value: (d) => d.contract.society?.email },
  { header: 'Garant adresse', value: (d) => cell(guarantorAddress(d.contract.society)) },
  // Véhicule
  { header: 'Immatriculation', value: (d) => d.contract.vehicle?.registration },
  { header: 'Marque', value: (d) => d.contract.vehicle?.brand },
  { header: 'Modèle', value: (d) => d.contract.vehicle?.model },
  { header: 'Carburant', value: (d) => d.contract.vehicle?.fuel },
  // Garantie
  { header: 'Produit', value: (d) => d.contract.warranty?.name },
  { header: 'Durée', value: (d) => cell(durationLabel(d.contract.duration)) },
  { header: 'Statut contrat', value: (d) => cell(contractStatusLabel(d.contract.idStatus)) },
  // Souscription
  { header: 'Mode', value: (d) => paymentMode(d.contract.monthlyPaymentId) },
  { header: 'Montant', value: (d) => cell(subscriptionAmount(d.contract)) },
  { header: 'Date de création', value: (d) => cell(fmtDate(d.contract.dateCreation)) },
  { header: 'Date de souscription', value: (d) => cell(fmtDate(d.contract.dateSouscription)) },
  { header: 'Statut souscription', value: (d) => cell(subscriptionStatusLabel(d.subscription)) },
  { header: 'Prochaine échéance', value: (d) => cell(fmtUnix(d.subscription?.nextPaymentDate)) },
  { header: 'Échéances payées', value: (d) => cell(paidInstallments(d.progress)) },
  { header: 'Montant restant (€)', value: (d) => csvAmount(d.progress?.remainingAmount) },
];
